/*
 * Expresso cutoff + stale Pickeasy dates.
 *
 * Expresso is the Pickeasy tab labeled "Expresso" (#standard-shipping-option-div).
 * That tab is Pickeasy's Standard Shipping fulfillment. Checkout carrier rates,
 * including Frenet Sedex, are reached through it. Lalamove same-day is the
 * separate rate "Entrega no Mesmo Dia" (code LALAMOVE_CARRIER_SERVICE).
 *
 * feat/hide-lalamove-shipping called element.remove() on the whole Standard
 * Shipping cell and Sedex disappeared with it. This script never removes
 * that node, Entrega, Retirada, or any Sedex/Frenet row. When Expresso is
 * closed it hides the cell whose label is exactly "Expresso" (display only,
 * and only when that cell does not also list Sedex) plus the Lalamove rate
 * "Entrega no Mesmo Dia" in the theme calculators. cart.attributes.expresso_ok
 * is 1 or 0 so a SMART Function can drop the Lalamove rate at checkout.
 *
 * Clock: Date header from /cart.js (store server time), converted to
 * America/Sao_Paulo. Liquid `now` is only an initial paint hint.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (root && root.document) {
    api.start(root);
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var CUTOFF_SECONDS = 16 * 3600 + 40 * 60;
  var DATE_ATTRIBUTES = [
    'Order Due Date',
    'Order Due Time',
    'Translated Order Due Date',
    'Translated Order Due Time'
  ];

  function pad2(n) {
    n = String(n);
    return n.length < 2 ? '0' + n : n;
  }

  function parseToYmd(raw) {
    var s = String(raw || '').trim();
    var m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return m[1] + '-' + pad2(m[2]) + '-' + pad2(m[3]);
    m = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
    if (m) return m[1] + '-' + pad2(m[2]) + '-' + pad2(m[3]);
    m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})/);
    if (!m) return null;
    var day = parseInt(m[1], 10);
    var month = parseInt(m[2], 10);
    if (day < 1 || day > 31 || month < 1 || month > 12) return null;
    return m[3] + '-' + pad2(month) + '-' + pad2(day);
  }

  function parseHolidays(source) {
    var list = [];
    if (Array.isArray(source)) {
      list = source;
    } else if (source && typeof source === 'object') {
      list = source.dates || source.holidays || [];
    } else {
      var text = String(source || '').trim();
      if (!text) return [];
      if (text.charAt(0) === '[' || text.charAt(0) === '{') {
        try {
          return parseHolidays(JSON.parse(text));
        } catch (err) {
          /* fall through to line split */
        }
      }
      list = text.split(/[\s,;]+/);
    }
    var out = [];
    var seen = {};
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      var ymd = parseToYmd(typeof item === 'string' ? item : item && item.date);
      if (ymd && !seen[ymd]) {
        seen[ymd] = true;
        out.push(ymd);
      }
    }
    return out;
  }

  function spParts(ms) {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
      hourCycle: 'h23'
    });
    var map = {};
    var parts = fmt.formatToParts(new Date(ms));
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type !== 'literal') map[parts[i].type] = parts[i].value;
    }
    var hour = parseInt(map.hour, 10);
    if (hour === 24) hour = 0;
    var dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return {
      ymd: map.year + '-' + map.month + '-' + map.day,
      hour: hour,
      minute: parseInt(map.minute, 10),
      second: parseInt(map.second, 10),
      dow: dowMap[map.weekday]
    };
  }

  function expressoAllowed(parts, holidays) {
    if (!parts || parts.dow === 0) return false;
    if (holidays && holidays.indexOf(parts.ymd) !== -1) return false;
    var seconds = parts.hour * 3600 + parts.minute * 60 + parts.second;
    return seconds <= CUTOFF_SECONDS;
  }

  function msUntilBoundary(ms, holidays) {
    var parts = spParts(ms);
    var seconds = parts.hour * 3600 + parts.minute * 60 + parts.second;
    var holiday = holidays && holidays.indexOf(parts.ymd) !== -1;
    var target = (parts.dow === 0 || holiday || seconds > CUTOFF_SECONDS)
      ? 24 * 3600
      : CUTOFF_SECONDS + 1;
    var delta = target - seconds;
    if (delta < 1) delta = 1;
    return delta * 1000 + 250;
  }

  function rateKind(blob) {
    var text = String(blob || '').toLowerCase();
    if (!text.trim()) return '';
    if (text.indexOf('sedex') !== -1 || text.indexOf('frenet') !== -1) return 'sedex';
    if (
      text.indexOf('lalamove') !== -1 ||
      text.indexOf('entrega no mesmo dia') !== -1 ||
      text.indexOf('lalamove_carrier') !== -1
    ) {
      return 'lalamove';
    }
    return '';
  }

  function rateKindFromEnterprise(enterprise) {
    if (!enterprise) return '';
    return rateKind([
      enterprise.name,
      enterprise.presentment_name,
      enterprise.code,
      enterprise.source
    ].join(' '));
  }

  function unpaddedYmd(ymd) {
    var p = String(ymd || '').split('-');
    if (p.length !== 3) return '';
    return String(Number(p[2])) + '-' + String(Number(p[1])) + '-' + p[0];
  }

  function attributeDateYmd(attributes) {
    if (!attributes) return null;
    var keys = ['Order Due Date', 'Translated Order Due Date'];
    for (var i = 0; i < keys.length; i++) {
      var ymd = parseToYmd(attributes[keys[i]]);
      if (ymd) return ymd;
    }
    return null;
  }

  function stripInstoreDate(value, todayYmd) {
    var raw = String(value || '');
    if (!raw || raw.indexOf('DD=') === -1) return null;
    var pieces = raw.split('&');
    var dd = null;
    var kept = [];
    for (var i = 0; i < pieces.length; i++) {
      var piece = pieces[i];
      if (piece.indexOf('DD=') === 0) {
        dd = decodeURIComponent(piece.slice(3).replace(/\+/g, ' '));
      } else if (piece.indexOf('DT=') !== 0) {
        kept.push(piece);
      }
    }
    var ymd = parseToYmd(dd);
    if (!ymd || ymd >= todayYmd) return null;
    return kept.filter(Boolean).join('&');
  }

  function start(root) {
    var config = root.PDB_EXPRESSO_CONFIG || {};
    var holidays = parseHolidays(config.holidaySource);
    var state = {
      skew: 0,
      allowed: !root.document.documentElement.classList.contains('pdb-expresso-blocked') &&
        !(root.document.body && root.document.body.classList.contains('pdb-expresso-blocked')),
      timer: 0,
      queue: Promise.resolve(),
      clearedStale: false
    };

    function serverNow() {
      return Date.now() + state.skew;
    }

    function ensureStyles() {
      if (root.document.getElementById('pdb-expresso-cutoff-styles')) return;
      var style = root.document.createElement('style');
      style.id = 'pdb-expresso-cutoff-styles';
      style.textContent = [
        '.pdb-expresso-blocked .pdb-expresso-tab,',
        '.pdb-expresso-blocked .cart-ship-rate-row[data-pdb-rate="lalamove"],',
        '.pdb-expresso-blocked .shipping-rate-row[data-pdb-rate="lalamove"] {',
        '  display: none !important;',
        '}'
      ].join('\n');
      root.document.head.appendChild(style);
    }

    function markExpressoTabs(doc) {
      var cells = doc.querySelectorAll('.ft-options-cell');
      for (var i = 0; i < cells.length; i++) {
        var cell = cells[i];
        var label = cell.querySelector('.label-container');
        var text = label ? label.textContent.replace(/\s+/g, ' ').trim() : '';
        var blob = (cell.innerText || '').toLowerCase();
        var sharesSedex = blob.indexOf('sedex') !== -1 || blob.indexOf('frenet') !== -1;
        if (/^expresso$/i.test(text) && !sharesSedex) cell.classList.add('pdb-expresso-tab');
        else cell.classList.remove('pdb-expresso-tab');
      }
    }

    function markRates(scope) {
      var rootEl = scope || root.document;
      markExpressoTabs(rootEl);
      var rows = rootEl.querySelectorAll('.cart-ship-rate-row, .shipping-rate-row');
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var existing = row.getAttribute('data-pdb-rate');
        if (existing === 'sedex' || existing === 'lalamove') continue;
        var nameEl = row.querySelector('.cart-ship-rate-name, .shipping-rate-name');
        var kind = rateKind(nameEl ? nameEl.textContent : '');
        if (kind) row.setAttribute('data-pdb-rate', kind);
      }
      if (!state.allowed) settleHiddenExpresso(rootEl);
    }

    function settleHiddenExpresso(doc) {
      var active = doc.querySelector('.pdb-expresso-tab .ft-options-active');
      if (!active) return;
      var entrega = doc.querySelector('#delivery-option-div');
      if (entrega && entrega !== active) entrega.click();
    }

    function applyDom(allowed) {
      state.allowed = allowed;
      root.document.documentElement.classList.toggle('pdb-expresso-blocked', !allowed);
      if (root.document.body) {
        root.document.body.classList.toggle('pdb-expresso-blocked', !allowed);
      }
      markRates(root.document);
    }

    function armTimer() {
      if (state.timer) root.clearTimeout(state.timer);
      var wait = msUntilBoundary(serverNow(), holidays);
      state.timer = root.setTimeout(function () {
        enqueue(syncCart);
      }, wait);
    }

    function enqueue(fn) {
      state.queue = state.queue.then(fn, fn);
      return state.queue;
    }

    function syncCart() {
      return fetch('/cart.js', {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      }).then(function (response) {
        var header = response.headers.get('Date');
        var serverMs = header ? Date.parse(header) : NaN;
        if (!isNaN(serverMs)) state.skew = serverMs - Date.now();
        return response.json().then(function (cart) {
          return { cart: cart, now: isNaN(serverMs) ? Date.now() : serverMs };
        });
      }).then(function (result) {
        var parts = spParts(result.now);
        var allowed = expressoAllowed(parts, holidays);
        applyDom(allowed);
        armTimer();
        return writeCart(result.cart, parts, allowed);
      }).catch(function (err) {
        if (root.console && root.console.error) {
          root.console.error('[expresso cutoff]', err);
        }
      });
    }

    function writeCart(cart, parts, allowed) {
      var attributes = cart.attributes || {};
      var updates = {};
      var desiredOk = allowed ? '1' : '0';
      if (String(attributes.expresso_ok || '') !== desiredOk) {
        updates.expresso_ok = desiredOk;
      }

      var dueYmd = attributeDateYmd(attributes);
      if (config.cartPage && !state.clearedStale && dueYmd && dueYmd < parts.ymd) {
        DATE_ATTRIBUTES.forEach(function (key) {
          if (attributes[key]) updates[key] = '';
        });
        if (attributes.is_today) updates.is_today = '';
      } else if (dueYmd) {
        var isToday = dueYmd === parts.ymd ? 'true' : 'false';
        if (String(attributes.is_today || '') !== isToday) updates.is_today = isToday;
      }

      var lineEdits = [];
      if (config.cartPage && !state.clearedStale && cart.items && cart.items.length) {
        cart.items.forEach(function (item, index) {
          var props = item.properties || {};
          var current = props._instoreAppId;
          if (!current) return;
          var next = stripInstoreDate(current, parts.ymd);
          if (next === null || next === current) return;
          var nextProps = {};
          Object.keys(props).forEach(function (key) {
            nextProps[key] = props[key];
          });
          nextProps._instoreAppId = next;
          lineEdits.push({ line: index + 1, quantity: item.quantity, properties: nextProps });
        });
      }

      var chain = Promise.resolve();
      if (Object.keys(updates).length) {
        chain = chain.then(function () {
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes: updates })
          });
        });
      }
      lineEdits.forEach(function (edit) {
        chain = chain.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(edit)
          });
        });
      });
      return chain.then(function () {
        if (config.cartPage) state.clearedStale = true;
      });
    }

    ensureStyles();
    applyDom(state.allowed);
    if (root.document.body) {
      var observer = new MutationObserver(function () {
        markRates(root.document);
      });
      observer.observe(root.document.body, { childList: true, subtree: true });
    }
    root.document.addEventListener('visibilitychange', function () {
      if (root.document.visibilityState === 'visible') enqueue(syncCart);
    });
    root.document.addEventListener('cart:updated', function () {
      enqueue(syncCart);
    });
    enqueue(syncCart);

    root.PDBExpresso = {
      rateKind: rateKind,
      rateKindFromEnterprise: rateKindFromEnterprise,
      refresh: function () { return enqueue(syncCart); }
    };
  }

  return {
    parseToYmd: parseToYmd,
    parseHolidays: parseHolidays,
    spParts: spParts,
    expressoAllowed: expressoAllowed,
    msUntilBoundary: msUntilBoundary,
    rateKind: rateKind,
    rateKindFromEnterprise: rateKindFromEnterprise,
    unpaddedYmd: unpaddedYmd,
    attributeDateYmd: attributeDateYmd,
    stripInstoreDate: stripInstoreDate,
    CUTOFF_SECONDS: CUTOFF_SECONDS,
    start: start
  };
});
